/**
 * ChaseTheAce Standard - Style-specific sections for GridEngine
 * Clean card design with partner logo, 3-column stats grid, countdown, CTA
 */
import React from 'react'
import Calendar from 'lucide-react/dist/esm/icons/calendar.js';
import DollarSign from 'lucide-react/dist/esm/icons/dollar-sign.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import Spade from 'lucide-react/dist/esm/icons/spade.js';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import Info from 'lucide-react/dist/esm/icons/info.js';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit'
import CountdownTimerStyles from '../../../components/CountdownTimerStyles'
import HeroPartnerGlassSelect from '../../../components/HeroPartnerGlassSelect'
import { useContent } from '../../../contexts/ContentContext'
import { useLanguage } from '../../../contexts/LanguageContext'
import { usePurchaseNavigate } from '../../../hooks/usePurchaseNavigate'
import { useCountdown } from '../../../hooks/useCountdown'
import { useCountdownStyle } from '../../../contexts/CountdownStyleContext'
import { useSiteConfig } from '../../../contexts/SiteConfigContext'
import { resolveCtaTargetCard, formatTargetCardName, formatCtaTargetCardText, resolveCtaManualEnvelopeMode } from '../../../utils/ctaHelpers'

/**
 * Hero section - Partner logo + 3-column stats (Jackpot/Cards/Weekly) + countdown + CTA
 */
export function ChaseStandardHeroSection({
  mainDraw,
  partner,
  onLoginClick,
  editMode,
  handleTranslationUpdate,
  sections = [],
}) {
  const { c } = useContent()
  const purchaseNavigate = usePurchaseNavigate(onLoginClick)
  const { countdownStyle } = useCountdownStyle()
  const { timeLeft, shouldAnimate } = useCountdown(mainDraw, { enableAnimation: true })

  const jackpotAmount = Math.floor(mainDraw?.current_jackpot ?? 0)
  const weeklyPrize = Math.floor(mainDraw?.weekly_prize ?? mainDraw?.secondary_prize ?? 0)
  const cardsRemaining = mainDraw?.cards_remaining ?? 52
  const { language } = useLanguage()
  const ctaTarget = resolveCtaTargetCard(sections)
  const ctaTargetName = formatTargetCardName(ctaTarget.rank, ctaTarget.suit, language)
  const targetCopy = (key, fallback) => formatCtaTargetCardText(c(key, fallback), ctaTarget, fallback, language)

  const drawDateLabel = mainDraw?.draw_date
    ? new Date(mainDraw.draw_date).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : ''

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 md:p-8">
      <div className="text-center mb-6">
        {partner?.partner_logo_url && (
          <div className="flex justify-center mb-4">
            <img
              src={partner.partner_logo_url}
              alt={partner.partner_name || 'Charity Logo'}
              className="h-16 md:h-20 object-contain"
            />
          </div>
        )}
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-2">
          <InlineTranslationEdit
            translationKey="chaseTheAce.title"
            value={c('chaseTheAce.title')}
            fallback="Chase the Ace"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          <InlineTranslationEdit
            translationKey="chaseTheAce.subtitle"
            value={targetCopy('chaseTheAce.subtitle', `Find the ${ctaTargetName} and win the jackpot!`)}
            fallback={`Find the ${ctaTargetName} and win the jackpot!`}
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </p>
        <HeroPartnerGlassSelect className="mx-auto mt-6 w-full max-w-md" />
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Jackpot */}
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/30 dark:to-emerald-800/30 rounded-xl p-5 border border-emerald-200 dark:border-emerald-700">
          <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide mb-2">
            <DollarSign className="w-4 h-4" />
            <InlineTranslationEdit
              translationKey="chaseTheAce.jackpot"
              value={c('chaseTheAce.jackpot')}
              fallback="Jackpot"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </div>
          <div className="text-3xl md:text-4xl font-black text-emerald-700 dark:text-emerald-300">
            ${jackpotAmount.toLocaleString()}
          </div>
        </div>

        {/* Cards Remaining */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-xl p-5 border border-blue-200 dark:border-blue-700">
          <div className="flex items-center gap-2 text-sm font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-2">
            <Spade className="w-4 h-4" />
            <InlineTranslationEdit
              translationKey="chaseTheAce.cardsRemaining"
              value={c('chaseTheAce.cardsRemaining')}
              fallback="Cards Remaining"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </div>
          <div className="text-3xl md:text-4xl font-black text-blue-700 dark:text-blue-300">
            {cardsRemaining} <span className="text-lg font-normal text-blue-500 dark:text-blue-400">/ 52</span>
          </div>
        </div>

        {/* Weekly Prize */}
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 rounded-xl p-5 border border-purple-200 dark:border-purple-700">
          <div className="flex items-center gap-2 text-sm font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide mb-2">
            <Trophy className="w-4 h-4" />
            <InlineTranslationEdit
              translationKey="chaseTheAce.weeklyPrize"
              value={c('chaseTheAce.weeklyPrize')}
              fallback="Weekly Prize"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </div>
          <div className="text-3xl md:text-4xl font-black text-purple-700 dark:text-purple-300">
            ${weeklyPrize.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Countdown Timer */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-6">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
          <Calendar className="w-4 h-4" />
          <InlineTranslationEdit
            translationKey="chaseTheAce.nextDraw"
            value={c('chaseTheAce.nextDraw')}
            fallback="Next Draw"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
          {drawDateLabel && (
            <span className="ml-2 text-gray-700 dark:text-gray-300">- {drawDateLabel}</span>
          )}
        </div>
        <CountdownTimerStyles
          timeLeft={timeLeft}
          shouldAnimate={shouldAnimate}
          style={countdownStyle}
        />
      </div>

      {/* CTA Button */}
      <div className="text-center">
        <button
          onClick={() => purchaseNavigate('/chase-the-ace-purchase')}
          className="inline-flex items-center gap-2 px-8 py-4 bg-charity-primary text-white font-bold text-lg rounded-lg shadow-lg hover:bg-charity-primary-dark transition-colors"
        >
          <Ticket className="w-5 h-5" />
          <InlineTranslationEdit
            translationKey="chaseTheAce.buyTickets"
            value={c('chaseTheAce.buyTickets')}
            fallback="Buy Tickets"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </button>
      </div>
    </div>
  )
}

/**
 * How It Works section - 4 steps with colored numbered circles
 */
export function ChaseStandardHowItWorksSection({ partner, editMode, handleTranslationUpdate, sections = [] }) {
  const { c } = useContent()
  const { language } = useLanguage()
  const { siteDetails } = useSiteConfig() || {}
  const isManualEnvelopeMode = resolveCtaManualEnvelopeMode(siteDetails, sections)
  const ctaTarget = resolveCtaTargetCard(sections)
  const ctaTargetName = formatTargetCardName(ctaTarget.rank, ctaTarget.suit, language)
  const targetCopy = (key, fallback) => formatCtaTargetCardText(c(key, fallback), ctaTarget, fallback, language)

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <Info className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">
          <InlineTranslationEdit
            translationKey="chaseTheAce.howItWorksTitle"
            value={targetCopy('chaseTheAce.howItWorksTitle', 'How It Works')}
            fallback="How It Works"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex gap-3">
          <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold text-sm">
            1
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
              <InlineTranslationEdit
                translationKey="chaseTheAce.step1Title"
                value={targetCopy('chaseTheAce.step1Title', isManualEnvelopeMode ? "Buy a Ticket" : "Buy a Ticket & Pick Envelope")}
                fallback={isManualEnvelopeMode ? "Buy a Ticket" : "Buy a Ticket & Pick Envelope"}
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              <InlineTranslationEdit
                translationKey="chaseTheAce.step1Desc"
                value={targetCopy('chaseTheAce.step1Desc', isManualEnvelopeMode ? "Purchase tickets for the weekly draw" : "Purchase tickets and choose your envelope")}
                fallback={isManualEnvelopeMode ? "Purchase tickets for the weekly draw" : "Purchase tickets and choose your envelope"}
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold text-sm">
            2
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
              <InlineTranslationEdit
                translationKey="chaseTheAce.step2Title"
                value={targetCopy('chaseTheAce.step2Title', 'Weekly Draw')}
                fallback="Weekly Draw"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              <InlineTranslationEdit
                translationKey="chaseTheAce.step2Desc"
                value={targetCopy('chaseTheAce.step2Desc', 'A winning ticket is drawn each week')}
                fallback="A winning ticket is drawn each week"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold text-sm">
            3
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
              <InlineTranslationEdit
                translationKey="chaseTheAce.step3Title"
                value={targetCopy('chaseTheAce.step3Title', isManualEnvelopeMode ? "Pick an Envelope" : "Reveal the Envelope")}
                fallback={isManualEnvelopeMode ? "Pick an Envelope" : "Reveal the Envelope"}
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              <InlineTranslationEdit
                translationKey="chaseTheAce.step3Desc"
                value={targetCopy('chaseTheAce.step3Desc', isManualEnvelopeMode ? "Winner picks an envelope from the deck" : "The winner's chosen envelope is opened to reveal the card")}
                fallback={isManualEnvelopeMode ? "Winner picks an envelope from the deck" : "The winner's chosen envelope is opened to reveal the card"}
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          <div className="flex-shrink-0 w-8 h-8 bg-emerald-100 dark:bg-emerald-900/50 rounded-full flex items-center justify-center text-emerald-600 dark:text-emerald-400 font-bold text-sm">
            4
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
              <InlineTranslationEdit
                translationKey="chaseTheAce.step4Title"
                value={targetCopy('chaseTheAce.step4Title', `Find the ${ctaTargetName}!`)}
                fallback={`Find the ${ctaTargetName}!`}
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              <InlineTranslationEdit
                translationKey="chaseTheAce.step4Desc"
                value={targetCopy('chaseTheAce.step4Desc', `Find the ${ctaTargetName} to win the jackpot!`)}
                fallback={`Find the ${ctaTargetName} to win the jackpot!`}
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </p>
          </div>
        </div>
      </div>

      {/* Partner Description */}
      {partner?.partner_description && (
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            {partner.partner_description}
          </p>
        </div>
      )}
    </div>
  )
}
